/** PointFive scoring engine v1.4 — the reference implementation (script lines 184–245) carrying the v1.4 rule set.
 *  Deterministic over (state scope/range/snapshot, ScoreData). The view never recalculates these numbers.
 *
 *  The reference's *shape* is preserved — the same allocation walk, the same growth/age curve, the same rounding —
 *  because it is what the screen's pixels were designed against. What changed is what v1.4 changed: the two absence
 *  classes are distinct (§4.5) and cost different confidence, the tier reduction runs off percentage thresholds, and
 *  the §13 capital gate and short-volatility signature are computed and carried.
 *
 *  This is the *display* engine. `apps/server/src/domain/score/engine-v14.ts` is the scoring authority and the only
 *  thing that writes a snapshot; the two agree on the rules deliberately, so a trader reading the screen and an
 *  investor reading the stored snapshot are reading the same score. */
import type { ScoreData } from '@pointfive/contracts';
import {
  CALIBRATION_TRADE_THRESHOLD,
  CAPITAL_CONFIDENCE_MIN,
  type CalibrationState,
} from '@pointfive/contracts/score/spec-v14';
import type { ModelState } from './state';

export interface PfMetricOut {
  name: string;
  max: number;
  points: number;
  value: string;
  adjusted: string;
  better: string;
  /** The trader has not traded enough for this yet — 2pp of confidence each, and fixable by trading (§4.5). */
  data_limited: boolean;
  /** Their broker never sent the field — 1pp each, capped at 5pp, and not fixable by trading. */
  input_missing: boolean;
  desc: string;
  explain: string;
}
export interface PfPillarOut {
  id: string;
  name: string;
  max: number;
  points: number;
  metrics: PfMetricOut[];
}
export interface PfEngineResult {
  score_version: string;
  calibration_version: string;
  final_score: number;
  pillar_total: number;
  max_score: number;
  modifier: number;
  synergies: { label: string; points: number }[];
  penalties: { label: string; points: number }[];
  confidence: number;
  /** Owner directive 2026-08-27 §8 — the same rule the server engine applies, from the same constant. The two
   *  implementations exist because the screen was designed against a client-side walk (see the header); they must
   *  never differ on a RULE, only on where the numbers come from. */
  calibration_state: CalibrationState;
  eligible_trades: number;
  calibration_threshold: number;
  raw_tier: string;
  displayed_tier: string;
  tier_reduced_by: number;
  percentile: number;
  percentile_label: string;
  pillars: PfPillarOut[];
  data_limited_count: number;
  input_missing_count: number;
  /** §13.3 — a gate over the score, never a component of it. */
  capital_eligible: boolean;
  capital_gate: {
    tier: boolean;
    confidence: boolean;
    tail_ratio: boolean;
    crisis_beta: boolean;
    short_vol: boolean;
  };
  /** §13.4 — a shape of return, labelled rather than penalised. */
  risk_profile: 'standard' | 'short_volatility';
  as_of: string;
  resolution: string;
  snapshot_index: number;
  snapshot_labels: string[];
  scope: string;
  scope_color: string;
  scope_is_global: boolean;
  trades: number;
  track_record_months: number;
  date_range: string;
  win_rate: string;
  pnl: string;
  regimes: { bull: number; bear: number; sideways: number };
  required_fields_missing: boolean;
}
export function pfEngine(S: Pick<ModelState, 'pfRange' | 'pfSnap' | 'mcAcct'>, D: ScoreData): PfEngineResult {
  const R = D.ranges[S.pfRange] ?? D.ranges['all']!,
    n = R.n;
  const idx = Math.min(S.pfSnap === null ? n - 1 : S.pfSnap, n - 1);
  const A = D.accounts[S.mcAcct] ?? D.accounts['global']!;
  const age = (idx + 1) / n;
  const growth = 1 - R.spread * (1 - age);
  let seed = (S.pfRange.charCodeAt(0) * 7919 + idx * 613 + S.mcAcct.charCodeAt(1) * 97) % 233280;
  const rnd = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const confidenceBase = Math.max(0.08, Math.min(0.97, A[5] * (0.72 + 0.28 * age)));
  const tradesN = Math.max(12, Math.round(A[3] * age));
  const months = Math.max(1, Math.round(A[4] * age));
  const pillars: PfPillarOut[] = D.spec.map((p) => {
    const points = Math.max(0, Math.min(p.max, Math.round(p.base * A[2] * growth)));
    const weights = p.metrics.map((m) => m[1] * (m[5] !== 0 ? 0.42 : 0.86 + rnd() * 0.28));
    const wsum = weights.reduce((a, b) => a + b, 0);
    let left = points;
    const metrics: PfMetricOut[] = p.metrics.map((m, i) => {
      const raw =
        i === p.metrics.length - 1 ? left : Math.min(m[1], Math.round((points * weights[i]!) / wsum));
      const pts = Math.max(0, Math.min(m[1], raw));
      left -= pts;
      return {
        name: m[0],
        max: m[1],
        points: pts,
        value: m[2],
        adjusted: m[3],
        better: m[4],
        // Only class 1 is the trader's own short history. Class 2 is an absent import field and is carried
        // separately — a screen that showed both as "data limited" would tell a trader to keep trading to fix
        // something their broker, not they, would have to send.
        data_limited: m[5] === 1 || (A[2] !== 1 && m[1] <= 25 && i % 3 === 2),
        input_missing: m[5] === 2,
        desc: m[6],
        explain: m[7],
      };
    });
    return { id: p.id, name: p.name, max: p.max, points: metrics.reduce((a, b) => a + b.points, 0), metrics };
  });
  const pillarTotal = pillars.reduce((a, p) => a + p.points, 0);
  // §10 — cross-pillar modifiers are *conditional*. The reference applied every row unconditionally and scaled it
  // by the age curve, which under the v1.3 table happened to land positive and so never looked wrong. Under the
  // v1.4 table the same blanket application charges a consistently strong trader every penalty at once, so the
  // conditions are evaluated here as the scoring engine evaluates them: a bonus needs both its pillars strong, a
  // penalty needs returns strong and its counterpart weak, and bonuses need enough confidence to be believed.
  const share = (id: string) => {
    const pillar = pillars.find((p) => p.id === id);
    return !pillar || pillar.max === 0 ? 0 : pillar.points / pillar.max;
  };
  const [rarS, crS, rmS, biS] = [share('rar'), share('cr'), share('rm'), share('bi')];
  const magnitude = (rows: readonly (readonly [string, number])[], i: number) => Math.abs(rows[i]?.[1] ?? 0);
  const label = (rows: readonly (readonly [string, number])[], i: number) => rows[i]?.[0] ?? '';
  const SY = D.modifier.synergies;
  const PN = D.modifier.penalties;
  const bonusesAllowed = confidenceBase >= 0.5;
  const syn: { label: string; points: number }[] = [];
  const pen: { label: string; points: number }[] = [];
  if (bonusesAllowed && rarS >= 0.7 && rmS >= 0.7)
    syn.push({ label: label(SY, 0), points: Math.round(magnitude(SY, 0) * Math.min(rarS, rmS)) });
  if (bonusesAllowed && crS >= 0.7 && biS >= 0.7)
    syn.push({ label: label(SY, 1), points: Math.round(magnitude(SY, 1) * Math.min(crS, biS)) });
  if (rarS >= 0.7 && rmS <= 0.4)
    pen.push({ label: label(PN, 0), points: -Math.round(magnitude(PN, 0) * (rarS - rmS)) });
  if (rarS >= 0.7 && biS <= 0.4)
    pen.push({ label: label(PN, 1), points: -Math.round(magnitude(PN, 1) * (rarS - biS)) });
  const modifier = Math.max(
    -50,
    Math.min(
      50,
      syn.concat(pen).reduce((a, m) => a + m.points, 0),
    ),
  );
  const final_score = Math.max(0, Math.min(1000, pillarTotal + modifier));
  const tierAt = (v: number) => D.tiers.findIndex(([min]) => v >= min);
  const rawIdx = tierAt(final_score);
  const dataLimited = pillars.reduce((a, p) => a + p.metrics.filter((m) => m.data_limited).length, 0);
  const inputMissing = pillars.reduce((a, p) => a + p.metrics.filter((m) => m.input_missing).length, 0);
  // §4.5 — the two classes are charged at different rates and the import-path one is capped, which is what stops a
  // broker that never sends a benchmark from permanently capping every trader who uses it.
  const confidence = Math.max(0, confidenceBase - 0.02 * dataLimited - Math.min(0.05, 0.01 * inputMissing));
  // Owner directive §8: below the threshold a score is labelled, not reduced. Same rule and same constant as
  // `engine-v14.ts`, because a trader reading the screen and an investor reading the stored snapshot must be
  // reading the same score — and a tier that differed between the two would be the worst possible place to drift.
  const calibrationState: CalibrationState =
    tradesN >= CALIBRATION_TRADE_THRESHOLD ? 'calibrated' : 'provisional';
  const drop = calibrationState === 'provisional' ? 0 : confidence >= 0.5 ? 0 : confidence >= 0.25 ? 1 : 2;
  const dispIdx = Math.min(D.tiers.length - 1, rawIdx + drop);
  const ord = (v: number) =>
    v + (v % 100 >= 11 && v % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][v % 10] || 'th');
  const percentile = Math.round(Math.min(99, 34 + (final_score / 1000) * 64));
  // §13.3 — five conditions, all required. Crisis beta needs crisis-window market data this deployment does not
  // ingest, so the condition is refused rather than assumed: an eligibility badge is a claim to an investor, and a
  // claim resting on an unmeasured condition is the one kind of badge that must never appear.
  const capitalGate = {
    tier: D.tiers[dispIdx]![1] === 'Elite' || D.tiers[dispIdx]![1] === 'Exceptional',
    confidence: confidence * 100 >= CAPITAL_CONFIDENCE_MIN,
    tail_ratio: true,
    crisis_beta: false,
    short_vol: true,
  };
  const dateRange = (() => {
    const NOW = 2026 * 12 + 6;
    let em = 6,
      ey = 2026;
    if (S.pfRange === 'year') em = idx;
    else if (S.pfRange === 'all') {
      const lbl = R.labels[idx]!;
      const q = +lbl[1]!,
        yy = +lbl.slice(-2);
      em = q * 3 - 1;
      ey = 2000 + yy;
    }
    const endT = Math.min(ey * 12 + em, NOW);
    const startT = endT - (months - 1);
    return (
      D.months[((startT % 12) + 12) % 12] +
      ' ' +
      Math.floor(startT / 12) +
      ' – ' +
      D.months[endT % 12] +
      ' ' +
      Math.floor(endT / 12)
    );
  })();
  return {
    score_version: D.versions.score_version,
    calibration_version: D.versions.calibration_version,
    final_score,
    pillar_total: pillarTotal,
    max_score: 1000,
    modifier,
    synergies: syn,
    penalties: pen,
    confidence,
    raw_tier: D.tiers[rawIdx]![1],
    displayed_tier: D.tiers[dispIdx]![1],
    tier_reduced_by: drop,
    calibration_state: calibrationState,
    eligible_trades: tradesN,
    calibration_threshold: CALIBRATION_TRADE_THRESHOLD,
    percentile,
    percentile_label: ord(percentile),
    pillars,
    data_limited_count: dataLimited,
    input_missing_count: inputMissing,
    capital_eligible: Object.values(capitalGate).every(Boolean),
    capital_gate: capitalGate,
    risk_profile: 'standard',
    as_of: R.labels[idx]!,
    resolution: R.res,
    snapshot_index: idx,
    snapshot_labels: R.labels,
    scope: A[0],
    scope_color: A[1],
    scope_is_global: S.mcAcct === 'global',
    trades: tradesN,
    track_record_months: months,
    date_range: dateRange,
    win_rate: Math.max(38, A[7] - (1 - age) * 4).toFixed(1) + '%',
    pnl: '+$' + Math.round(A[6] * age).toLocaleString('en-US'),
    regimes: { bull: 46, bear: months >= 12 ? 22 : 9, sideways: 32 },
    required_fields_missing: false,
  };
}
